import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { getButtonVariantProps, getTrellisButtonVariantProps } from 'pricing-pages-ui-components/utils/buttonVariantUtils';
import SingleProductCTA from '../../../features/purchaseMotions/components/SingleProductCTA';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../../hooks/useBulkPurchaseOptions';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppSelector } from '../../../reducers';
import { isMerchCardCTAsDataLoadingSelector } from '../../../selectors/LoadingStateSelectors';
import { productSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtilsLegacy';
import { ErrorBoundary } from '../../../v2/components/error-boundary/ErrorBoundary';
import { BUNDLE_FEATURE_TABLE_HEADER } from '../../../v2/constants/tracking/TrackingSources';
import { useHasApiNameInProductInventory } from '../../../v2/hooks/inventory/useProductInventoryLookup';
import CodeSplitEnvWrapper from '../../common/env/CodeSplitEnvWrapper';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import FeatureTableColTag from '../FeatureTableColTag';
import FeatureTableFreePriceDisplay from '../FeatureTableFreePriceDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const GenericBundleFeatureTableHeaderCell = ({
  trackingSource,
  screen,
  apiName,
  badgeName
}) => {
  const isAppReady = useGlobalLoading();
  const isFree = apiName === 'hubspot-free';
  const {
    hasProductInInventory: isCurrentPlan,
    loading: isCurrentPlanLoading
  } = useHasApiNameInProductInventory({
    apiName
  });
  const product = useAppSelector(state => {
    return productSelector(state, {
      apiName
    });
  });
  const isMerchCardCTAsDataLoading = useAppSelector(isMerchCardCTAsDataLoadingSelector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  const {
    purchaseOptionsForTerm: pricingInfo,
    loading: pricingInfoLoading
  } = usePurchaseableProductsForTerm({
    apiName,
    queryParamTerm
  });
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId: product === null || product === void 0 ? void 0 : product.currentSkuId
  });
  const loading = pricingInfoLoading || !isAppReady || isCurrentPlanLoading;
  if (!product && !loading) return null;
  const buttonVariantProps = getButtonVariantProps({
    variant: 'table-header'
  });
  const trellisCtaVariantProps = getTrellisButtonVariantProps({
    variant: 'table-header'
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
      apiName: apiName,
      badgeName: badgeName
    }), /*#__PURE__*/_jsx(Text, {
      as: "strong",
      typography: "heading-50",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `products.${apiName}`
      })
    }), /*#__PURE__*/_jsx(View, {
      marginBlock: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: loading,
        heights: [20],
        widths: [140],
        children: isFree ? /*#__PURE__*/_jsx(FeatureTableFreePriceDisplay, {}) : pricingInfo && /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
          pricingInfo: pricingInfo,
          seleniumId: apiName || 'loading',
          currencyCode: currencyCode,
          currencyCodeUnit: currencyCodeUnit
        })
      })
    }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
      isLoading: isMerchCardCTAsDataLoading,
      heights: [40],
      widths: [190],
      children: /*#__PURE__*/_jsx(View, Object.assign({}, trellisCtaVariantProps, {
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: apiName ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            trackingSource: trackingSource,
            screen: screen
          }) : null,
          children: isFree ? /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui/components/CurrentPlanButton`),
            publicComponentLoader: () => import( /* webpackChunkName: "public-chunk" */`pricing-pages-ui/components/common/SignupButton`),
            authedComponentProps: Object.assign({}, getTrellisButtonVariantProps({
              variant: 'table-header',
              visible: isCurrentPlan
            })),
            publicComponentProps: Object.assign({
              sourceId: BUNDLE_FEATURE_TABLE_HEADER,
              trackingSource: BUNDLE_FEATURE_TABLE_HEADER,
              use: 'primary'
            }, buttonVariantProps)
          }) : /*#__PURE__*/_jsx(SingleProductCTA, Object.assign({
            screen: screen,
            apiName: apiName,
            trackingSource: trackingSource,
            purchaseMotion: purchaseMotions[0],
            purchaseMotionLocation: 'GENERIC' // replace with FEATURE_TABLE when available https://git.hubteam.com/HubSpot/pricing-pages-unified-ui/issues/11974
          }, buttonVariantProps))
        })
      }))
    })]
  });
};
export default GenericBundleFeatureTableHeaderCell;